import React, { useState } from 'react';
import { api, isSpendApprovalError } from './api';
import { toast } from '../hooks/use-toast';
import { X, FlaskConical, Send, Loader2, Users, Split, UserPlus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import CRMRecipientPicker from './CRMRecipientPicker';
import DraftActions from './DraftActions';

/** Split-send two subject lines to the same audience so you can see which one gets more clicks. */
export default function ABTestModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [subjectA, setSubjectA] = useState('');
  const [subjectB, setSubjectB] = useState('');
  const [html, setHtml] = useState('');
  const [rawRecipients, setRawRecipients] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  // email -> name, kept for the CRM picks so {{name}} can be filled; typed addresses have none.
  const [names, setNames] = useState({});

  // Parse the recipient textarea → dedupe + validate. One address per line or comma-separated.
  const parseRecipients = () => {
    const seen = new Set();
    const list = [];
    for (const chunk of rawRecipients.split(/[\n,;]/)) {
      const email = chunk.trim().toLowerCase();
      if (!email) continue;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) continue;
      if (seen.has(email)) continue;
      seen.add(email);
      list.push(names[email] ? { email, name: names[email] } : { email });
    }
    return list;
  };

  const recipients = parseRecipients();
  const splitEstimate = recipients.length;
  const halfA = Math.ceil(splitEstimate / 2);
  const halfB = splitEstimate - halfA;
  const alreadyInSet = new Set(recipients.map((r) => r.email));

  // Merge CRM picks into the textarea (dedupe). The textarea stays email-only for
  // readability; the picks' names ride along in `names` and go out with the send.
  const handleCRMPicks = (picks) => {
    setNames((prev) => {
      const next = { ...prev };
      picks.forEach((p) => { if (p.name) next[p.email.toLowerCase()] = p.name; });
      return next;
    });
    const existing = rawRecipients.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean);
    const existingLower = new Set(existing.map((e) => e.toLowerCase()));
    const additions = picks
      .map((p) => p.email)
      .filter((e) => !existingLower.has(e.toLowerCase()));
    if (additions.length === 0) {
      toast({ title: 'All picks already in your list' });
      return;
    }
    const joined = [...existing, ...additions].join('\n');
    setRawRecipients(joined);
    toast({ title: `Added ${additions.length} recipient${additions.length === 1 ? '' : 's'} from CRM` });
  };

  const submit = async () => {
    if (!name.trim() || !subjectA.trim() || !subjectB.trim() || !html.trim()) {
      toast({ title: 'Fill in every field first' });
      return;
    }
    if (recipients.length < 2) {
      toast({ title: 'Need at least 2 valid emails to split-send' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/email/ab-test', {
        name,
        subject_a: subjectA,
        subject_b: subjectB,
        html,
        recipients,
      });
      toast({ title: 'A/B test launched', description: `Variant A → ${data.variant_a_count} · Variant B → ${data.variant_b_count}` });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (isSpendApprovalError(e)) return;
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' && detail?.message
        ? detail.message
        : (typeof detail === 'string' ? detail : 'Failed to launch A/B test');
      toast({ title: 'Launch failed', description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ab-test-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1"><FlaskConical size={11} /> A/B Subject Line Test</div>
            <div className="font-serif text-xl font-bold text-white">Split-send two subject lines to find the winner</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white" data-testid="ab-test-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <Label>Test Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Q1 Refi Promo — subject test" className="mt-1" data-testid="ab-test-name" />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-white" style={{ background: '#3b82f6' }}>A</span> Subject Line A</Label>
              <Input value={subjectA} onChange={(e) => setSubjectA(e.target.value)} placeholder="Rates are dropping — refi now" className="mt-1" data-testid="ab-test-subject-a" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-white" style={{ background: '#6366f1' }}>B</span> Subject Line B</Label>
              <Input value={subjectB} onChange={(e) => setSubjectB(e.target.value)} placeholder="Your refi savings estimate is ready" className="mt-1" data-testid="ab-test-subject-b" />
            </div>
          </div>

          <div>
            <Label>Email Body (HTML)</Label>
            <Textarea rows={5} value={html} onChange={(e) => setHtml(e.target.value)} placeholder="<p>Hi {{name}}, ...</p>" className="mt-1 font-mono text-[12px]" data-testid="ab-test-html" />
            <div className="text-[10px] text-slate-500 mt-1">Both variants use the same body — only the subject line changes so clicks are a clean comparison. Merge fields: {'{{name}}'}, {'{{first_name}}'}, {'{{email}}'} (a contact with no name reads “there”).</div>
            <div className="text-[10px] text-slate-500 mt-1" data-testid="ab-test-footer-note">Every email ends with your business address (Branding settings) and an unsubscribe link. They are added for you and cannot be removed.</div>
            <DraftActions subject={subjectA} html={html} />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Users size={12} /> Recipients (one email per line or comma-separated)</Label>
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                data-testid="ab-test-open-crm-picker"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white hover:brightness-110"
                style={{ background: '#6BA82E' }}
              >
                <UserPlus size={11} /> Pick from CRM
              </button>
            </div>
            <Textarea
              rows={4}
              value={rawRecipients}
              onChange={(e) => setRawRecipients(e.target.value)}
              placeholder="john@example.com&#10;jane@example.com&#10;…or click Pick from CRM to add leads/contacts"
              className="mt-1 font-mono text-[12px]"
              data-testid="ab-test-recipients"
            />
            {splitEstimate > 0 && (
              <div className="mt-2 flex items-center gap-3 text-[11px]" data-testid="ab-test-split-preview">
                <span className="inline-flex items-center gap-1 text-slate-600"><Split size={11} /> {splitEstimate} recipient{splitEstimate !== 1 ? 's' : ''} will split:</span>
                <span className="px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: '#3b82f6' }}>A → ~{halfA}</span>
                <span className="px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: '#6366f1' }}>B → ~{halfB}</span>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">The winner is the subject line with more clicks (opens are shown but not counted: Apple Mail inflates them). Nothing is sent to the rest of your list. Each variant needs 10 delivered emails first.</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-100" data-testid="ab-test-cancel">Cancel</button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="ab-test-submit"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {busy ? 'Launching…' : 'Launch A/B Test'}
            </button>
          </div>
        </div>
      </div>

      <CRMRecipientPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onConfirm={handleCRMPicks}
        excludeEmails={alreadyInSet}
      />
    </div>
  );
}
